import React, { useEffect, useState } from "react";
import { Crown, Copy, TrendingUp, TrendingDown } from "lucide-react";
import { api, inr } from "@/lib/api";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { Marks, copyTv } from "@/pages/VaCross";
import { GoldScorecard } from "@/components/GoldScorecard";
import { useFlow, FlowTags, InTime } from "@/components/FlowMarks";

const MODES = [["swing", "TFR Gold (Swing)"], ["intraday", "TFR Gold (Intraday)"]];
const RULES = {
  swing: { up: "above Monthly Node-H · crossed Weekly Node-H", down: "below Monthly Node-L · crossed Weekly Node-L" },
  intraday: { up: "above Monthly Node-H · crossed or above Weekly Node-H · crossed Daily Node-H", down: "below Monthly Node-L · crossed or below Weekly Node-L · crossed Daily Node-L" },
};
const pct = (v) => <span className={v >= 0 ? "text-emerald-600" : "text-rose-600"}>{v > 0 ? "+" : ""}{v}%</span>;
const fmtV = (v) => (v >= 1e7 ? `${(v / 1e7).toFixed(2)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(2)}L` : v?.toLocaleString("en-IN"));

const Row = ({ r, hot, side, m, inAt }) => {
  const V = side === "up" ? "Node-H" : "Node-L";
  return (
    <div className="flex items-center gap-1 border-b border-slate-200">
      <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
        <button className="flex-1 min-w-0 flex items-center gap-2 text-xs font-mono px-2 py-1.5 hover:bg-blue-500/10 transition-colors text-left" data-testid={`gold-row-${side}-${r.symbol}`}>
          <span className="flex-1 min-w-0 text-slate-900 font-semibold">{r.symbol}<span className="ml-1"><InTime t={inAt} testid={`gold-in-${side}-${r.symbol}`} /></span><FlowTags m={m} testid={`gold-${side}-${r.symbol}`} /><Marks r={r} hot={hot} />
            <span className="block text-[9px] text-slate-500 font-normal">
              M {V} {inr(r.m_level)} · W {V} {inr(r.w_level)} {r.w_at ? `@ ${r.w_at}` : "(already beyond)"}{r.d_at ? ` · D ${V} ${inr(r.d_level)} @ ${r.d_at}` : ""}
            </span>
          </span>
          <span className="w-28 text-right" title={r.open_avg ? `First 5 min candle vs its usual ${fmtV(r.open_avg)} (avg of past sessions)` : "Not enough opening history"} data-testid={`gold-open-${side}-${r.symbol}`}>
            <span className="text-slate-700">{r.open_vol ? fmtV(r.open_vol) : "—"}</span>
            {r.open_x != null && <span className={`ml-1 text-[9px] px-1 font-bold ${r.open_x >= 3 ? "bg-amber-400 text-black" : r.open_x >= 2 ? "bg-amber-500/20 text-amber-300 border border-amber-500/50" : "text-slate-500"}`}>{r.open_x}×</span>}
          </span>
          <span className="w-20 text-right text-slate-800">{inr(r.ltp)}</span>
          <span className="w-14 text-right" title="Distance from Monthly level">{pct(r.m_dist_pct)}</span>
          <span className="w-14 text-right" title="Day change">{pct(r.change_pct)}</span>
        </button>
      </StockMenu>
      <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} />
    </div>
  );
};

const later = (...t) => t.filter(Boolean).sort().pop();

const Side = ({ side, rows, hot, mode, flow }) => (
  <div className="border border-slate-200 bg-white" data-testid={`gold-list-${mode}-${side}`}>
    <div className={`flex items-center gap-1.5 px-2 h-9 text-xs font-bold font-mono border-b border-slate-200 ${side === "up" ? "text-emerald-600" : "text-rose-600"}`}>
      {side === "up" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {side === "up" ? "Bullish" : "Bearish"} <span className="text-slate-500 font-normal" data-testid={`gold-count-${mode}-${side}`}>{rows.length}</span>
      <span className="ml-2 text-[10px] font-normal text-slate-500 truncate">{RULES[mode][side]}</span>
    </div>
    <div className="flex gap-2 px-2 py-1 text-[9px] uppercase text-slate-500 font-mono border-b border-slate-200">
      <span className="flex-1">Stock · levels · cross time</span><span className="w-28 text-right">Open 5m vol · ×</span><span className="w-20 text-right">Price</span><span className="w-14 text-right">vs M</span><span className="w-14 text-right">Day %</span><span className="w-7" />
    </div>
    {rows.length ? rows.map((r) => <Row key={r.symbol} r={r} hot={hot} side={side} m={flow.marks[r.symbol]} inAt={mode === "intraday" ? later(r.w_at, r.d_at) : r.w_at} />)
      : <p className="p-4 text-xs font-mono text-slate-500" data-testid={`gold-empty-${mode}-${side}`}>No stock qualified yet today</p>}
  </div>
);

export default function TfrGold() {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState("swing");
  const [bigOpen, setBigOpen] = useState(false);
  useEffect(() => {
    const load = () => api.get("/today/tfr-gold").then((r) => setD(r.data)).catch(() => setD({ tabs: {} }));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const flow = useFlow();
  const keepFor = (k, rows = []) => { const r = k === "intraday" ? flow.filter(rows) : rows; return bigOpen ? r.filter((x) => x.open_x >= 3) : r; };
  const keep = (rows) => keepFor(mode, rows);
  const raw = d?.tabs?.[mode] || { up: [], down: [] };
  const t = { up: keep(raw.up), down: keep(raw.down) };
  return (
    <div className="space-y-3" data-testid="gold-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Crown className="h-5 w-5 text-amber-400" /> TFR Gold</h1>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">price beyond last month's TFR Value Node + fresh 5 min close through this week's (and for Intraday, today's) Node · F&O stocks + NIFTY, BANKNIFTY · session {d?.session || "—"}
          {d && !d.monthly_ready ? " · monthly levels are being built" : ""}</p>
      </div>
      <div className="flex flex-wrap items-center gap-1" data-testid="gold-tabs">
        {MODES.map(([k, label]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`gold-tab-${k}`}
            className={`h-9 px-4 text-xs font-mono font-bold border transition-colors ${mode === k ? "border-amber-400 bg-amber-400/15 text-slate-900" : "border-slate-300 text-slate-500 hover:text-slate-900"}`}>
            {label} <span className="text-slate-500">{d ? keepFor(k, d.tabs?.[k]?.up).length + keepFor(k, d.tabs?.[k]?.down).length : ""}</span>
          </button>
        ))}
        <button onClick={() => setBigOpen(!bigOpen)} aria-pressed={bigOpen} data-testid="gold-open3-toggle"
          className={`h-8 px-3 ml-2 text-xs font-mono border transition-colors ${bigOpen ? "bg-amber-400 text-black border-amber-400 font-bold" : "border-slate-300 text-slate-500 hover:border-amber-400"}`}>
          Opening 3×+ only
        </button>
        <button onClick={() => copyTv([...t.up, ...t.down])} data-testid="gold-copy-tv"
          className="ml-auto h-8 px-3 text-xs font-mono border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors flex items-center gap-1.5"><Copy className="h-3.5 w-3.5 text-blue-600" /> Copy to TradingView</button>
      </div>
      {!d ? <p className="p-6 text-xs font-mono text-slate-500">Loading…</p> : (
        <div className="grid gap-3 xl:grid-cols-2">
          <Side side="up" rows={t.up} hot={d.hot} mode={mode} flow={flow} />
          <Side side="down" rows={t.down} hot={d.hot} mode={mode} flow={flow} />
        </div>
      )}
      {mode === "intraday" && <p className="text-[10px] font-mono text-slate-500" data-testid="gold-flow-note">Intraday shows only stocks with opening volume or Big Money ≥ 3× · IN = time the stock entered this scanner</p>}
      <GoldScorecard />
    </div>
  );
}
